import { Fragment, useState, useEffect, useCallback, type ComponentType, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import {
  Plus,
  LogOut,
  FileText,
  Settings,
  Shield,
  Palette,
  ArrowLeft,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
  Globe,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
} from "lucide-react";
import {
  HeaderActivityIcon,
  TabFeedIcon,
  TabPodcastsIcon,
  TabProfileIcon,
  TabSearchIcon,
} from "@/components/icons/TabIcons";
import { SCROLL_LOCKUP_SPECS, ScrollSymbol } from "@/components/ScrollSymbol";
import { AppIcon } from "@/components/ui/app-icon";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useSidebarCollapsed } from "@/hooks/useSidebarCollapsed";
import { useTheme } from "@/lib/theme";
import { getDraftCount } from "@/lib/drafts";

type NavItem = {
  href: string;
  label: string;
  icon?: LucideIcon;
  tabIcon?: ComponentType<{ size?: number; color?: string; active?: boolean; className?: string }>;
  tabIconSize?: number;
  match: (loc: string) => boolean;
  badge?: number;
};

const SIDEBAR_TAB_ICON_SIZE = 24;

function MaybeTooltip({
  label,
  show,
  children,
}: {
  label: string;
  show: boolean;
  children: ReactNode;
}) {
  if (!show) return <>{children}</>;
  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} className="text-[12px] px-2 py-1">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export default function DesktopSidebar() {
  const [location] = useLocation();
  const { user, signOut } = useAuth();
  const { mode, setMode } = useTheme();
  const [draftCount, setDraftCount] = useState(0);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [accountPanel, setAccountPanel] = useState<"menu" | "appearance">("menu");
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [peekExpanded, setPeekExpanded] = useState(false);
  const { collapsed, toggle } = useSidebarCollapsed();
  const displayCollapsed = collapsed && !peekExpanded;

  useEffect(() => {
    if (!collapsed) setPeekExpanded(false);
  }, [collapsed]);

  useEffect(() => {
    if (collapsed && !sidebarHovered && !accountMenuOpen) setPeekExpanded(false);
  }, [accountMenuOpen, collapsed, sidebarHovered]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "b") return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      setPeekExpanded(false);
      toggle();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  const refreshDraftCount = useCallback(() => {
    setDraftCount(getDraftCount());
  }, []);

  useEffect(() => {
    refreshDraftCount();
    window.addEventListener("draftsChanged", refreshDraftCount);
    return () => window.removeEventListener("draftsChanged", refreshDraftCount);
  }, [refreshDraftCount]);

  const profilePath = "/profile";
  const fallbackInitial = (user?.displayName || user?.username || "?").slice(0, 1).toUpperCase();

  const navItems: NavItem[] = [
    { href: "/home", label: "Home", tabIcon: TabFeedIcon, match: (loc) => loc === "/home" },
    { href: "/podcasts", label: "Podcasts", tabIcon: TabPodcastsIcon, tabIconSize: 26, match: (loc) => loc.startsWith("/podcast") },
    { href: "/activity", label: "Activity", tabIcon: HeaderActivityIcon, match: (loc) => loc === "/activity" },
    { href: profilePath, label: "Profile", tabIcon: TabProfileIcon, match: (loc) => loc === profilePath },
  ];

  const itemClass = (active: boolean) =>
    `flex h-10 w-full items-center rounded-lg text-[14px] ${
      active ? "font-semibold" : "font-medium"
    } ${
      displayCollapsed
        ? "text-(--scroll-text)"
        : `sidebar-nav-item ${active ? "sidebar-nav-item-active" : ""}`
    }`;

  const iconSlotClass = (active = false) =>
    `h-10 w-10 flex items-center justify-center shrink-0 relative ${
      displayCollapsed
        ? `rounded-lg sidebar-nav-item ${active ? "sidebar-nav-item-active" : ""}`
        : ""
    }`;
  // Hide labels by opacity the instant the sidebar collapses, rather than letting
  // the overflow clip sweep across them — otherwise the first letter lingers at the
  // collapse boundary for a frame (most visible when a hover-peek retracts over the
  // page content). The width animation still plays; the labels just don't flash.
  const labelClass = `min-w-0 truncate pl-1.5 ${displayCollapsed ? "opacity-0" : ""}`;
  const headerLockupSpec = SCROLL_LOCKUP_SPECS.header;
  const badgeAccentStyle = { backgroundColor: user?.accentColor || "#1D1916" };

  const renderBadge = (badge?: number) => {
    if (badge == null || badge <= 0) return null;
    if (displayCollapsed) {
      return (
        <span
          className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full ring-2 ring-(--butter-bg-elevated)"
          style={badgeAccentStyle}
          aria-label={`${badge} unread`}
        />
      );
    }
    return (
      <span
        className="ml-auto text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1"
        style={badgeAccentStyle}
      >
        {badge > 99 ? "99+" : badge}
      </span>
    );
  };

  const renderNavIcon = (item: NavItem, active: boolean) => {
    if (item.tabIcon) {
      const Icon = item.tabIcon;
      return <Icon size={item.tabIconSize ?? SIDEBAR_TAB_ICON_SIZE} color="currentColor" active={active} />;
    }
    if (item.icon) return <AppIcon icon={item.icon} size="md" />;
    return null;
  };

  return (
    <aside
      className={`desktop-sidebar-shell hidden md:block my-2 shrink-0 relative z-50 overflow-visible transition-[width] duration-150 ease-out ${collapsed ? "w-[60px]" : "w-[260px]"}`}
      data-collapsed={displayCollapsed}
      onMouseEnter={() => {
        setSidebarHovered(true);
        if (collapsed) setPeekExpanded(true);
      }}
      onMouseLeave={() => {
        setSidebarHovered(false);
        if (collapsed && !accountMenuOpen) setPeekExpanded(false);
      }}
    >
      <div
        className={`desktop-sidebar-panel bg-(--scroll-bg) transition-[width] duration-150 ease-out ${displayCollapsed ? "h-full w-[60px]" : "w-[260px] border-r border-(--scroll-border)"} ${
          collapsed && !displayCollapsed ? "absolute -inset-y-2 left-0 z-50 py-2" : "relative h-full"
        }`}
        data-floating={collapsed && !displayCollapsed ? "true" : undefined}
      >
      <div className={`h-full overflow-hidden transition-[width] duration-150 ease-out ${displayCollapsed ? "w-[54px]" : "w-[260px]"}`}>
      <div className="flex h-full w-[260px] flex-col">
      {/* Header: logo mark / expanded brand */}
      <div className="flex items-center px-2.5 pt-4 pb-6">
        <MaybeTooltip label="Home" show={displayCollapsed}>
          <Link
            aria-label="Home"
            href="/home"
            className="inline-flex h-10 w-full items-center text-(--scroll-text) cursor-pointer origin-left transition-transform duration-150 ease-out hover:scale-105 active:scale-100"
          >
            <span className={iconSlotClass()}>
              <ScrollSymbol size={22} />
            </span>
            <span
              className={labelClass}
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: headerLockupSpec.wordmark,
                fontWeight: 800,
                letterSpacing: headerLockupSpec.tracking,
                lineHeight: 1,
                transform: headerLockupSpec.drop
                  ? `translateY(${headerLockupSpec.drop}px)`
                  : undefined,
              }}
            >
              scroll
            </span>
          </Link>
        </MaybeTooltip>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto space-y-1.5 px-2.5">
        {navItems.map((item, idx) => {
          const active = item.match(location);
          const searchActive = location === "/search" || location === "/discover";
          return (
            <Fragment key={item.href}>
              <MaybeTooltip label={item.label} show={displayCollapsed}>
                <Link href={item.href} className={`${itemClass(active)} cursor-pointer`}>
                  <span className={iconSlotClass(active)}>
                    {renderNavIcon(item, active)}
                    {displayCollapsed && renderBadge(item.badge)}
                  </span>
                  <span className={labelClass}>{item.label}</span>
                  {!displayCollapsed && renderBadge(item.badge)}
                </Link>
              </MaybeTooltip>
              {idx === 0 && (
                <>
                  <MaybeTooltip label="Create" show={displayCollapsed}>
                    <button
                      className={`${itemClass(false)} ${displayCollapsed ? "" : "w-full"}`}
                      onClick={() => window.dispatchEvent(new Event("openCreate"))}
                    >
                      <span className={iconSlotClass()}>
                        <AppIcon icon={Plus} size="lg" strokeWidth={1.9} />
                      </span>
                      <span className={labelClass}>Create</span>
                    </button>
                  </MaybeTooltip>
                  <MaybeTooltip label="Search" show={displayCollapsed}>
                    <Link
                      href="/search"
                      className={`${itemClass(searchActive)} cursor-pointer`}
                    >
                      <span className={iconSlotClass(searchActive)}>
                        <TabSearchIcon size={SIDEBAR_TAB_ICON_SIZE} color="currentColor" active={searchActive} />
                      </span>
                      <span className={labelClass}>Search</span>
                    </Link>
                  </MaybeTooltip>
                </>
              )}
            </Fragment>
          );
        })}

        {/* Drafts (conditional) */}
        {draftCount > 0 && (
          <MaybeTooltip label={`Drafts (${draftCount})`} show={displayCollapsed}>
            <button
              className={`${itemClass(false)} ${displayCollapsed ? "" : "w-full"}`}
              onClick={() => window.dispatchEvent(new Event("openComposeWithDrafts"))}
            >
              <span className={iconSlotClass()}>
                <AppIcon icon={FileText} size="md" />
                {displayCollapsed && (
                  <span
                    className="absolute top-0.5 right-0.5 bg-(--scroll-text) w-2 h-2 rounded-full ring-2 ring-(--butter-bg-elevated)"
                    aria-label={`${draftCount} drafts`}
                  />
                )}
              </span>
              <span className={labelClass}>Drafts</span>
              {!displayCollapsed && (
                <span className="ml-auto bg-(--scroll-text) text-(--scroll-bg) text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                  {draftCount > 99 ? "99+" : draftCount}
                </span>
              )}
            </button>
          </MaybeTooltip>
        )}
      </nav>

      {/* Toggle button */}
      <div className="px-2.5 py-2">
        <MaybeTooltip label="Expand sidebar" show={displayCollapsed}>
          <button
            type="button"
            onClick={() => {
              setPeekExpanded(false);
              toggle();
            }}
            aria-label={
              displayCollapsed
                ? "Expand sidebar"
                : collapsed
                  ? "Keep sidebar expanded"
                  : "Collapse sidebar"
            }
            className={`flex h-10 w-full items-center rounded-lg text-[14px] font-medium text-(--scroll-text-tertiary) ${
              displayCollapsed ? "" : "sidebar-nav-item"
            }`}
          >
            <span className={iconSlotClass()}>
              <AppIcon icon={collapsed ? PanelLeftOpen : PanelLeftClose} size="md" />
            </span>
            <span className={labelClass}>
              {displayCollapsed ? "Expand sidebar" : collapsed ? "Keep expanded" : "Collapse"}
            </span>
            <span className="ml-auto pr-2 text-[11px] text-(--scroll-text-tertiary)">⌘B</span>
          </button>
        </MaybeTooltip>
      </div>

      {/* User row */}
      {user && (() => {
        const hasDistinctDisplayName = !!user.displayName && user.displayName !== user.username;
        const primary = hasDistinctDisplayName
          ? user.displayName
          : user.username
            ? `@${user.username}`
            : "";
        const secondary = user.username ? `@${user.username}` : "";

        return (
          <div className="flex items-center px-2.5 py-2.5">
            <Popover
              open={accountMenuOpen}
              onOpenChange={(open) => {
                setAccountMenuOpen(open);
                if (!open) setAccountPanel("menu");
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label="Account menu"
                  className={
                    displayCollapsed
                      ? "flex h-10 flex-1 min-w-0 items-center rounded-lg"
                      : "flex h-10 flex-1 min-w-0 items-center rounded-lg sidebar-nav-item"
                  }
                >
                  <span className={iconSlotClass()}>
                    <Avatar className="h-6 w-6 shrink-0">
                      <AvatarImage src={user.profileImageUrl ?? undefined} alt="" />
                      <AvatarFallback className="text-[10px]">{fallbackInitial}</AvatarFallback>
                    </Avatar>
                  </span>
                  <span className={`${labelClass} text-[14px] font-medium text-(--scroll-text)`}>
                    {primary}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent
                side={displayCollapsed ? "right" : "top"}
                align={displayCollapsed ? "end" : "start"}
                sideOffset={displayCollapsed ? 12 : 8}
                className={accountPanel === "appearance" ? "w-[320px] p-3" : "w-[248px] p-1"}
              >
                {accountPanel === "appearance" ? (
                  <div>
                    <div className="relative flex items-center justify-center pb-3">
                      <button
                        type="button"
                        onClick={() => setAccountPanel("menu")}
                        aria-label="Back to account menu"
                        className="absolute left-0 h-8 w-8 flex items-center justify-center rounded-lg sidebar-nav-item"
                      >
                        <AppIcon icon={ArrowLeft} size="md" />
                      </button>
                      <span className="text-[16px] font-semibold text-(--scroll-text)">Appearance</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1 rounded-2xl bg-(--scroll-bg-secondary) p-1">
                      {[
                        { value: "light" as const, label: "Light", icon: Sun },
                        { value: "dark" as const, label: "Dark", icon: Moon },
                        { value: "system" as const, label: "Auto", icon: Monitor },
                      ].map((option) => {
                        const active = mode === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => setMode(option.value)}
                            className={`flex h-14 items-center justify-center gap-1.5 rounded-xl text-[14px] font-semibold transition-colors ${
                              active
                                ? "bg-(--butter-bg-elevated) text-(--scroll-text) butter-shadow-card"
                                : "text-(--scroll-text-tertiary) hover:text-(--scroll-text)"
                            }`}
                            aria-pressed={active}
                            aria-label={option.label}
                          >
                            <AppIcon icon={option.icon} size="md" />
                            <span>{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <>
                    {secondary && (
                      <div className="px-2 pt-1.5 pb-1 text-[12px] text-(--scroll-text-tertiary) truncate">
                        {secondary}
                      </div>
                    )}
                    <Link
                      href="/settings/scroll-pro"
                      onClick={() => setAccountMenuOpen(false)}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] font-medium cursor-pointer sidebar-nav-item"
                    >
                      <span className="w-5 flex items-center justify-center shrink-0"><ScrollSymbol size={13} /></span>
                      <span className="truncate">Scroll Pro</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => setAccountPanel("appearance")}
                      className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-[13px] font-medium sidebar-nav-item"
                    >
                      <span className="w-5 flex items-center justify-center shrink-0"><AppIcon icon={Palette} size="sm" /></span>
                      <span className="truncate">Appearance</span>
                      <AppIcon icon={ChevronRight} size="sm" className="ml-auto text-(--scroll-text-tertiary)" />
                    </button>
                    <Link
                      href="/settings"
                      onClick={() => setAccountMenuOpen(false)}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] font-medium cursor-pointer sidebar-nav-item"
                    >
                      <span className="w-5 flex items-center justify-center shrink-0"><AppIcon icon={Settings} size="sm" /></span>
                      <span className="truncate">Settings</span>
                    </Link>
                    {user.isAdmin && (
                      <Link
                        href="/admin/usage"
                        onClick={() => setAccountMenuOpen(false)}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] font-medium cursor-pointer sidebar-nav-item"
                      >
                        <span className="w-5 flex items-center justify-center shrink-0"><AppIcon icon={Shield} size="sm" /></span>
                        <span className="truncate">Admin</span>
                      </Link>
                    )}
                    <div className="my-1 h-px bg-(--scroll-border)" />
                    <Link
                      href="/"
                      onClick={() => setAccountMenuOpen(false)}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] font-medium cursor-pointer sidebar-nav-item"
                    >
                      <span className="w-5 flex items-center justify-center shrink-0"><AppIcon icon={Globe} size="sm" /></span>
                      <span className="truncate">Visit Scroll's website</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        signOut({ redirectUrl: "/" });
                      }}
                      className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-[13px] font-medium sidebar-nav-item"
                    >
                      <span className="w-5 flex items-center justify-center shrink-0"><AppIcon icon={LogOut} size="sm" /></span>
                      <span className="truncate">Log out</span>
                    </button>
                  </>
                )}
              </PopoverContent>
            </Popover>
          </div>
        );
      })()}
      </div>
      </div>
      </div>
    </aside>
  );
}
